import React from "react";
import { Construction, Trash2 } from "lucide-react";

// ── Shared building blocks for the mocked/WIP section screens ──

const TONES = {
  green: "bg-[#e7f7ec] text-[#1f9d57]",
  amber: "bg-[#fdf3e2] text-[#b06f00]",
  red: "bg-[#fdecea] text-[#d23b2f]",
  blue: "bg-[#e8f0f9] text-[#2563a8]",
  violet: "bg-[#efe9fb] text-[#7a4fc0]",
  gray: "bg-black/[.05] text-[#6e6e73]",
};

export function StatusPill({ label, tone = "gray" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold px-2.5 py-1 rounded-full ${TONES[tone] || TONES.gray}`}>
      {label}
    </span>
  );
}

export function WipBanner() {
  return (
    <div className="flex items-center gap-2.5 rounded-[12px] border border-[#f0c36d]/50 bg-[#fff8e8] px-3.5 py-2.5" data-testid="wip-banner">
      <span className="flex items-center justify-center w-[24px] h-[24px] rounded-[7px] bg-[#f0c36d]/25 text-[#b06f00] flex-shrink-0">
        <Construction size={14} />
      </span>
      <span className="text-[12px] font-bold text-[#8a6d1f]">Work in progress</span>
      <span className="text-[11.5px] text-[#a07f2a] leading-snug">· Preview screen with <b>mocked data</b> — not yet wired to live APIs.</span>
    </div>
  );
}

export function MockPage({ id, title, subtitle, actions, children }) {
  return (
    <div className="flex flex-col gap-5 fade-up" data-testid={`${id}-view`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[24px] font-bold tracking-[-.7px] text-[#1d1d1f]" data-testid={`${id}-title`}>{title}</h1>
          {subtitle ? <p className="text-[12px] text-[#6e6e73] mt-1">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      <WipBanner />
      {children}
    </div>
  );
}

export function StatCards({ items }) {
  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
      {items.map((s) => (
        <div key={s.label} className="rounded-[16px] border border-black/[.07] bg-white/[.65] px-4 py-3.5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,.04)" }}>
          <div className="text-[9px] font-bold tracking-[.08em] uppercase text-[#aeaeb2]">{s.label}</div>
          <div className={`text-[22px] font-extrabold tracking-[-.6px] mt-1 tabular-nums ${s.tone === "red" ? "text-[#d23b2f]" : s.tone === "green" ? "text-[#1f9d57]" : "text-[#1d1d1f]"}`}>{s.value}</div>
          {s.sub ? <div className="text-[11px] text-[#6e6e73] mt-1">{s.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

// columns: [{ key, label, align }]. A cell value can be a string/number, or
// { pill: { label, tone } } to render a StatusPill.
export function DataTable({ columns, rows, testid }) {
  const alignCls = (a) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left");
  return (
    <div className="rounded-[16px] border border-black/[.07] bg-white/[.65] overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(0,0,0,.04)" }} data-testid={testid}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-black/[.02]">
            {columns.map((c) => (
              <th key={c.key} className={`px-4 py-2.5 text-[9.5px] font-bold tracking-[.06em] uppercase text-[#aeaeb2] ${alignCls(c.align)}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-black/[.05] hover:bg-black/[.02] transition-colors">
              {columns.map((c) => {
                const v = row[c.key];
                return (
                  <td key={c.key} className={`px-4 py-3 text-[12.5px] text-[#3a3a3c] ${alignCls(c.align)} ${c.mono ? "tabular-nums font-medium" : ""}`}>
                    {v && typeof v === "object" && v.pill ? <StatusPill {...v.pill} />
                      : v && typeof v === "object" && v.actions ? (
                        <div className="flex items-center justify-end gap-1.5">
                          {v.actions.map((a, ai) => (
                            <button key={ai} data-testid={a.testid} onClick={a.onClick} title={a.title || a.label}
                              className={a.icon
                                ? `w-[30px] h-[30px] rounded-[8px] border flex items-center justify-center transition-colors ${a.danger ? "border-red-200 text-red-500 hover:bg-red-50" : "border-black/[.12] text-[#6e6e73] hover:bg-black/[.04]"}`
                                : "text-[11px] font-semibold px-3 py-1.5 rounded-[8px] bg-white border border-black/[.12] text-[#3a3a3c] hover:bg-black/[.04] transition-colors"}>
                              {a.icon === "trash" ? <Trash2 size={14} /> : (a.label || "View")}
                            </button>
                          ))}
                        </div>
                      ) : v && typeof v === "object" && v.button ? (
                        <button data-testid={v.button.testid} onClick={v.button.onClick}
                          className="text-[11px] font-semibold px-3 py-1.5 rounded-[8px] bg-white border border-black/[.12] text-[#3a3a3c] hover:bg-black/[.04] transition-colors">
                          {v.button.label || "View"}
                        </button>
                      ) : v}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GhostButton({ children, ...p }) {
  return (
    <button className="text-[12px] font-semibold px-3 py-2 rounded-[10px] border border-black/[.1] bg-white hover:bg-black/[.04] text-[#1d1d1f] transition" {...p}>
      {children}
    </button>
  );
}
